/* eslint-disable @next/next/no-img-element -- Avatar overlays share absolute geometry. */
'use client';

import { usePartTuning, tunedPosition, frontHairMask, partTransformStyle, tunedFrontHairPercent } from '@/lib/avatars/usePartTuning';
import { useId } from 'react';
import { getImageProps } from 'next/image';
import { AVATAR_SLOTS, getAvatarPart } from '@/lib/avatars/parts';
import type { AvatarCustomization } from '@/types/game';

const TINTS = {
  platinum: [243, 220, 157],
  ginger: [198, 85, 30],
  silver: [203, 214, 229],
  blue_tips: [18, 168, 240],
  pink_streaks: [240, 69, 157],
} as const;

/** Shared by full previews, purchase modals, profile thumbnails and game avatars. */
export function AvatarLayers({ customization, placement = "front", assetResolver, imageSizes }: { assetResolver?: (asset: string) => string; customization: AvatarCustomization; placement?: "front" | "back"; imageSizes?: string }) {
  const tuning = usePartTuning();
  const filterId = useId().replaceAll(':', '');
  const color = customization.hairColor;
  const tint = color && color !== 'natural' ? TINTS[color] : undefined;
  const hideHair = getAvatarPart(customization.headwear)?.hideHair;
  const partial = color === 'blue_tips' || color === 'pink_streaks';
  return <>
    {tint && <svg width="0" height="0" aria-hidden="true" className="absolute">
      <defs><filter id={filterId} colorInterpolationFilters="sRGB">
        <feColorMatrix type="matrix" values={`.12 .24 .04 0 ${tint[0]/255*.75} .12 .24 .04 0 ${tint[1]/255*.75} .12 .24 .04 0 ${tint[2]/255*.75} 0 0 0 1 0`} />
      </filter></defs>
    </svg>}
    {AVATAR_SLOTS.map(slot => {
      const part = getAvatarPart(customization[slot]);
      if (!part || (slot === 'hair' && hideHair)) return null;
      const frontPercent = slot === 'hair' ? tunedFrontHairPercent(part, tuning) : undefined;
      const splitHair = frontPercent !== undefined;
      const hairBehindFace = slot === 'hair' && tuning.hairBehindFace?.[part.id] === true;
      if (placement === 'back' && part.id !== 'earwear_headphones' && !hairBehindFace && !splitHair) return null;
      if (placement === 'front' && hairBehindFace && !splitHair) return null;
      const colored = slot === 'hair' && part.id !== 'hair_zidane' && tint;
      const pos = tunedPosition(part, tuning);
      // Keep the existing overlay geometry, but request thumbnail pixels rather
      // than the 600px original. Dedicated asset resolvers retain their own CDN.
      const src = assetResolver?.(part.asset) ?? part.asset;
      const responsive = imageSizes && !assetResolver && src.startsWith('/assets/')
        ? getImageProps({ src, alt: '', width: 600, height: 600, quality: 60,
          sizes: imageSizes.replace(/(\d+(?:\.\d+)?)px(?=\s*(?:,|$))/g, (_, px: string) => `${Math.ceil(Number(px) * pos.width / 100)}px`),
        }).props
        : null;
      const image = { src: responsive?.src ?? src, srcSet: responsive?.srcSet, sizes: responsive?.sizes, loading: 'lazy' as const, decoding: 'async' as const };
      const position = { ...partTransformStyle(part, tuning), maskImage: splitHair && placement === "front" ? frontHairMask(frontPercent) : undefined, clipPath: part.id === 'earwear_headphones' && placement === 'front' ? 'inset(0 60% 0 0)' : part.clipPath, top: `${pos.top}%`, left: `${pos.left}%`, width: `${pos.width}%` };
      return <span key={slot} data-avatar-slot={slot} data-part-id={part.id}>
        <img {...image} alt="" className="pointer-events-none absolute object-contain" style={{ ...position, filter: colored && !partial ? `url(#${filterId})` : undefined }} />
        {colored && partial && <img {...image} alt="" className="pointer-events-none absolute object-contain" style={{ ...position, filter: `url(#${filterId})`, clipPath: color === 'blue_tips' ? 'inset(0 0 58% 0)' : 'polygon(38% 0, 53% 0, 65% 100%, 50% 100%)' }} />}
      </span>;
    })}
  </>;
}
